import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CalendarDays, Copy, Eye, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState(null);

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const response = await fetch(`${API}/menus`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMenus(data);
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // MOCK DATA FOR TUTORIAL
  const MOCK_HISTORY_MENUS = [
    {
      menu_id: 'demo',
      title: 'Healthy Mix',
      week_start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      week_end: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      created_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
      total_calories: 14200,
    },
    {
      menu_id: 'demo-2',
      title: 'Vegetarian Week',
      week_start: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      week_end: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      total_calories: 13500,
    }
  ];

  const { user } = useAuth();
  const showDemo = menus.length === 0 && !loading && user && !user.tutorial_pages_seen?.includes('history');

  // Use mock data if in demo mode
  const displayMenus = showDemo ? MOCK_HISTORY_MENUS : menus;

  const copyMenu = async (menuId) => {
    try {
      const response = await fetch(`${API}/menus/${menuId}/copy`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const newMenu = await response.json();
        toast.success(t('success.copied'));
        navigate(`/menu/${newMenu.menu_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error copying menu:', error);
      toast.error(t('errors.generic'));
    }
  };

  const deleteMenu = async () => {
    if (!menuToDelete) return;

    try {
      const response = await fetch(`${API}/menus/${menuToDelete.menu_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setMenus(menus.filter(m => m.menu_id !== menuToDelete.menu_id));
        toast.success(t('success.deleted'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleteDialogOpen(false);
      setMenuToDelete(null);
    }
  };

  const dateLocale = language === 'es' ? es : enUS;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="font-manrope font-bold text-3xl text-charcoal mb-8">
          {t('history.title')}
        </h1>

        {/* Menu List */}
        {displayMenus.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CalendarDays className="w-12 h-12 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-2xl text-charcoal mb-2">
              {t('history.empty')}
            </h2>
            <Button
              onClick={() => navigate('/menu/new')}
              className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 font-manrope font-bold"
              data-testid="create-first-menu-history-button"
            >
              {t('dashboard.generateMenu')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {displayMenus.map((menu, index) => (
              <Card
                key={menu.menu_id}
                className="bg-white rounded-2xl shadow-soft p-5 hover:shadow-lift transition-shadow animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`history-menu-${menu.menu_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-manrope font-semibold text-lg text-charcoal mb-1">
                      {menu.title}
                    </h3>
                    <p className="font-inter text-sm text-stone">
                      {menu.week_start && format(parseISO(menu.week_start), 'dd/MM/yyyy')} - {menu.week_end && format(parseISO(menu.week_end), 'dd/MM/yyyy')}
                    </p>
                    <p className="font-inter text-xs text-stone/70 mt-1">
                      {t('history.created')}: {format(new Date(menu.created_at), 'PPp', { locale: dateLocale })}
                    </p>
                    {menu.total_calories && (
                      <p className="font-inter text-sm text-sage mt-2">
                        {menu.total_calories} kcal {t('dashboard.weekTotal').toLowerCase()}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/menu/${menu.menu_id}`)}
                      className="rounded-full px-4"
                      data-testid={`view-menu-${menu.menu_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      {t('history.view')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => !showDemo && copyMenu(menu.menu_id)}
                      className="rounded-full px-4"
                      data-testid={`copy-menu-${menu.menu_id}`}
                      disabled={showDemo}
                    >
                      <Copy className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      {t('history.copy')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!showDemo) {
                          setMenuToDelete(menu);
                          setDeleteDialogOpen(true);
                        }
                      }}
                      className="rounded-full px-4 hover:text-destructive hover:border-destructive"
                      data-testid={`delete-menu-${menu.menu_id}`}
                      disabled={showDemo}
                    >
                      <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      {t('delete')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {t('delete')} "{menuToDelete?.title}"
              </DialogTitle>
            </DialogHeader>
            <p className="font-inter text-stone py-4">
              ¿Estás seguro de eliminar este menú?
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={deleteMenu}
                className="rounded-full"
                data-testid="confirm-delete-menu-button"
              >
                {t('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout >
  );
}
